import type { ReactNode } from 'react'
import { Sparkles, Star, Trash2, Undo2, X, ZoomIn } from 'lucide-react'
import { Spinner } from '../../lib/shadcn/spinner'
import { cn } from '../../lib/shadcn/utils'

// One photo in the product dialog. On a computer it's a small tile whose
// buttons show on hover. On a phone (no hover) it's a bigger tile with a
// labelled button row underneath, so each action is easy to hit.
export default function ProductPhotoTile({
  url,
  featured,
  isCleaning,
  isCleaned,
  onPreview,
  onSetFeatured,
  onRemove,
  onCleanup,
  onUseOriginal,
}: {
  url: string
  featured: boolean
  isCleaning: boolean
  isCleaned: boolean
  onPreview: () => void
  onSetFeatured: () => void
  onRemove: () => void
  onCleanup: () => void
  onUseOriginal: () => void
}) {
  const cleanupLabel = isCleaned ? 'Use original photo' : 'Clean up photo'

  return (
    <div className="flex w-[calc(50%-0.375rem)] flex-col gap-1.5 sm:w-24">
      <div
        className={cn(
          'group relative aspect-square w-full overflow-hidden rounded-md border bg-muted sm:h-24 sm:w-24',
          featured && 'ring-2 ring-primary',
        )}
      >
        <img
          src={url}
          alt="Product"
          title="Click to see it larger"
          onClick={onPreview}
          className="h-full w-full cursor-zoom-in object-cover"
        />

        {/* Hover buttons (computer) */}
        <button
          type="button"
          onClick={onSetFeatured}
          aria-label={featured ? 'Featured photo' : 'Set as featured photo'}
          className={cn(
            'absolute left-1 top-1 hidden rounded-full p-1 transition-colors sm:block',
            featured
              ? 'bg-primary text-primary-foreground'
              : 'bg-black/50 text-white opacity-0 hover:bg-black/70 group-hover:opacity-100',
          )}
        >
          <Star className="h-3.5 w-3.5" fill={featured ? 'currentColor' : 'none'} />
        </button>

        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove photo"
          className="absolute right-1 top-1 hidden rounded-full bg-black/50 p-1 text-white opacity-0 transition-colors hover:bg-black/70 group-hover:opacity-100 sm:block"
        >
          <X className="h-3.5 w-3.5" />
        </button>

        {!isCleaning && (
          <button
            type="button"
            onClick={isCleaned ? onUseOriginal : onCleanup}
            aria-label={cleanupLabel}
            title={isCleaned ? 'Use original photo' : 'Clean up photo with ChatGPT'}
            className={cn(
              'absolute right-1 hidden rounded-full bg-black/50 p-1 text-white opacity-0 transition-colors hover:bg-black/70 group-hover:opacity-100 sm:block',
              featured ? 'bottom-6' : 'bottom-1',
            )}
          >
            {isCleaned ? <Undo2 className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
          </button>
        )}

        {isCleaning && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-background/70 text-xs font-medium sm:text-[10px]">
            <Spinner className="h-5 w-5 sm:h-4 sm:w-4" />
            Cleaning up…
          </div>
        )}

        {featured && (
          <span className="absolute inset-x-0 bottom-0 bg-primary/90 py-0.5 text-center text-xs font-medium text-primary-foreground sm:text-[10px]">
            Featured
          </span>
        )}
      </div>

      {/* Button row (phone) */}
      <div className="grid grid-cols-4 gap-1 sm:hidden">
        <MobileAction
          label={featured ? 'Featured' : 'Feature'}
          onClick={onSetFeatured}
          active={featured}
          disabled={featured}
        >
          <Star className="h-4 w-4" fill={featured ? 'currentColor' : 'none'} />
        </MobileAction>
        <MobileAction label="Zoom" onClick={onPreview}>
          <ZoomIn className="h-4 w-4" />
        </MobileAction>
        <MobileAction
          label={isCleaned ? 'Original' : 'Clean up'}
          onClick={isCleaned ? onUseOriginal : onCleanup}
          disabled={isCleaning}
        >
          {isCleaned ? <Undo2 className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
        </MobileAction>
        <MobileAction label="Remove" onClick={onRemove} destructive>
          <Trash2 className="h-4 w-4" />
        </MobileAction>
      </div>
    </div>
  )
}

function MobileAction({
  label,
  onClick,
  children,
  active = false,
  disabled = false,
  destructive = false,
}: {
  label: string
  onClick: () => void
  children: ReactNode
  active?: boolean
  disabled?: boolean
  destructive?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={cn(
        'flex h-11 flex-col items-center justify-center gap-0.5 rounded-md border text-[10px] font-medium leading-none transition-colors disabled:cursor-default',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : destructive
            ? 'text-destructive hover:bg-destructive/10 active:bg-destructive/10'
            : 'text-foreground hover:bg-accent active:bg-accent disabled:opacity-50',
      )}
    >
      {children}
      {label}
    </button>
  )
}
